import React, { useRef, useEffect, useState } from "react";
import "./CarouselSlide.css";

const loadedDisplayKeys = new Set();

const CarouselSlide = ({
  item,
  position,
  audioEnabled = false,
  onDisplayReady = null,
}) => {
  const isActive = position === "current";
  const videoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(item.type !== "video");

  // Keep mute state live without resetting playback.
  useEffect(() => {
    if (videoRef.current && item.type === "video") {
      videoRef.current.muted = !audioEnabled;

      if (isActive && audioEnabled && videoRef.current.paused) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.log("Autoplay prevented:", err.message);
          });
        }
      }
    }
  }, [audioEnabled, isActive, item.type]);

  // Handle video play/pause when the active slide changes.
  useEffect(() => {
    if (videoRef.current && item.type === "video") {
      if (isActive) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.log("Autoplay prevented:", err.message);
          });
        }
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive, item.type, item.url]);

  useEffect(() => {
    setIsVideoReady(item.type !== "video");
  }, [item.type, item.url]);

  const isImage = item.type === "image" || item.type === "photo";
  const isVideo = item.type === "video";
  const shouldRenderVideo = isVideo && isActive;
  const hasPoster = Boolean(item.thumbnail_url);
  const displayKey = isVideo ? item.thumbnail_url || item.url : item.url;

  const markDisplayReady = () => {
    if (!displayKey) return;
    loadedDisplayKeys.add(displayKey);
    if (isActive && onDisplayReady) {
      onDisplayReady(displayKey);
    }
  };

  useEffect(() => {
    if (isActive && displayKey && loadedDisplayKeys.has(displayKey) && onDisplayReady) {
      onDisplayReady(displayKey);
    }
  }, [displayKey, isActive, onDisplayReady]);

  if (isVideo && !isActive) {
    return (
      <div className={`scroll_content ${position} placeholder`}>
        {hasPoster ? (
          <img
            src={item.thumbnail_url}
            style={item.style}
            alt={item.caption || "Video thumbnail"}
            className="carousel-media"
            loading="eager"
            decoding="async"
            fetchPriority="low"
            onLoad={markDisplayReady}
          />
        ) : (
          <video
            src={item.url}
            poster={item.thumbnail_url || undefined}
            style={item.style}
            className="carousel-media"
            muted
            playsInline
            preload="auto"
            onLoadedData={markDisplayReady}
            onCanPlay={markDisplayReady}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`scroll_content ${position}`}>
      {isImage ? (
        <img
          src={item.url}
          style={item.style}
          alt={item.caption || "Carousel image"}
          className="carousel-media"
          loading={isActive ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={isActive ? "high" : "low"}
          onLoad={markDisplayReady}
        />
      ) : (
        <>
          {hasPoster ? (
            <img
              src={item.thumbnail_url}
              style={item.style}
              alt={item.caption || "Video thumbnail"}
              className={`carousel-media carousel-media-poster${
                isVideoReady ? " is-hidden" : ""
              }`}
              loading="eager"
              decoding="async"
              fetchPriority={isActive ? "high" : "low"}
              onLoad={markDisplayReady}
            />
          ) : (
            <div
              className={`carousel-media video-placeholder${
                isVideoReady ? " is-hidden" : ""
              }`}
              style={item.style}
              aria-hidden="true"
            >
              <div className="video-placeholder-icon">▶</div>
            </div>
          )}
          {shouldRenderVideo && (
            <video
              ref={videoRef}
              src={item.url}
              poster={item.thumbnail_url || undefined}
              style={item.style}
              className={`carousel-media carousel-video${
                isVideoReady ? " is-ready" : ""
              }`}
              loop
              muted={!audioEnabled}
              playsInline
              preload="auto"
              autoPlay={isActive}
              controls={false}
              onLoadedData={() => {
                setIsVideoReady(true);
                markDisplayReady();
              }}
              onCanPlay={() => {
                setIsVideoReady(true);
                markDisplayReady();
              }}
              onPlaying={() => {
                setIsVideoReady(true);
                markDisplayReady();
              }}
              onWaiting={() => setIsVideoReady(false)}
              onStalled={() => setIsVideoReady(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CarouselSlide;
